<script setup>
/* global axios */
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useMapGetter } from 'dashboard/composables/store';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const { currentAccount } = useAccount();
const {
  captainEnabled,
  captainLimits,
  documentLimits,
  responseLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const isLoading = ref(true);
const billingData = ref(null);

const hasPlan = computed(() => {
  return !!billingData.value?.plan;
});

const planName = computed(() => billingData.value?.plan?.name || '—');
const billingCycleLabel = computed(
  () => billingData.value?.billing_cycle_label || '—'
);
const planPrice = computed(() => {
  const price = billingData.value?.price;
  if (!price) return '—';
  return `R$ ${Number(price).toFixed(2).replace('.', ',')}`;
});
const dueDateLabel = computed(() => billingData.value?.due_date_label || '—');
const checkoutUrl = computed(() => billingData.value?.checkout_url || '');
const showRenewalButton = computed(
  () => billingData.value?.show_renewal_button === true && !!checkoutUrl.value
);

const handleRenewalClick = () => {
  const url = checkoutUrl.value;
  if (!url) return;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch {
    // Invalid URL — ignore silently
  }
};

const limits = computed(() => billingData.value?.limits || {});

/**
 * Compute a percentage for meter display
 * @param {object} limitData - { allowed, consumed } | { limit, used, percentage }
 * @returns {{ consumed: number, total: number, percentage: number }}
 */
const meterData = limitData => {
  if (!limitData) return { consumed: 0, total: 0, percentage: 0 };

  // Conversation format has 'limit' and 'used' keys
  if ('limit' in limitData) {
    const total = limitData.limit || 0;
    const consumed = limitData.used || 0;
    const percentage = total > 0 ? Math.round((consumed / total) * 100) : 0;
    return { consumed, total, percentage };
  }

  // Standard format: allowed/consumed
  const total = limitData.allowed || 0;
  const consumed = limitData.consumed || 0;
  const percentage = total > 0 ? Math.round((consumed / total) * 100) : 0;
  return { consumed, total, percentage };
};

const agentsMeter = computed(() => meterData(limits.value.agents));
const inboxesMeter = computed(() => meterData(limits.value.inboxes));
const conversationsMeter = computed(() =>
  meterData(limits.value.conversations)
);
const aiCreditsMeter = computed(() => meterData(limits.value.ai_credits));

const fetchBillingData = async () => {
  try {
    isLoading.value = true;
    const accountId = currentAccount.value.id;
    const response = await axios.get(`/api/v1/accounts/${accountId}/billing`);
    billingData.value = response.data?.billing || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Billing] Failed to fetch billing data:', error);
    billingData.value = null;
  } finally {
    isLoading.value = false;
  }
};

// ─── Support Widget (same logic as SidebarProfileMenu) ───
const globalConfig = useMapGetter('globalConfig/get');
const currentUser = useMapGetter('getCurrentUser');
const supportWidgetBaseUrl = computed(
  () => globalConfig.value?.supportWidgetBaseUrl
);
const remoteSdkLoaded = ref(false);

const isValidSupportUrl = url => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

const injectBubbleHideCSS = () => {
  if (document.getElementById('support-widget-bubble-hide')) return;
  const style = document.createElement('style');
  style.id = 'support-widget-bubble-hide';
  style.textContent = '#cw-bubble-holder { display: none !important; }';
  document.head.appendChild(style);
};

const loadRemoteSdk = () => {
  return new Promise((resolve, reject) => {
    if (remoteSdkLoaded.value) {
      resolve();
      return;
    }
    const rawUrl = supportWidgetBaseUrl.value;
    if (!isValidSupportUrl(rawUrl)) {
      reject(new Error('Invalid SUPPORT_WIDGET_BASE_URL'));
      return;
    }
    const baseUrl = rawUrl.replace(/\/+$/, '');
    window.chatwootSettings = { hideMessageBubble: true, position: 'left' };
    injectBubbleHideCSS();
    const script = document.createElement('script');
    script.src = `${baseUrl}/packs/js/sdk.js`;
    script.async = true;
    script.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: globalConfig.value.chatwootInboxToken,
          baseUrl,
        });
        remoteSdkLoaded.value = true;
        let resolved = false;
        const done = () => {
          if (!resolved) {
            resolved = true;
            if (window.$chatwoot && currentUser.value) {
              const user = currentUser.value;
              window.$chatwoot.setUser(user.email || `user_${user.id}`, {
                name: user.name || user.available_name,
                email: user.email,
                avatar_url: user.avatar_url,
              });
            }
            resolve();
          }
        };
        window.addEventListener('chatwoot:ready', done, { once: true });
        setTimeout(done, 3000);
      } else {
        reject(new Error('SDK not available after script load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load support SDK'));
    document.head.appendChild(script);
  });
};

// ─── Click-outside and focus-tracking listeners (matches SidebarProfileMenu) ───
let onClickOutside;
let iframeFocused = false;
let removeAllWidgetListeners;

const onWindowBlur = () => {
  iframeFocused = true;
};

const onWindowFocus = () => {
  if (iframeFocused && window.$chatwoot && window.$chatwoot.isOpen) {
    window.$chatwoot.toggle('close');
    if (removeAllWidgetListeners) removeAllWidgetListeners();
  }
  iframeFocused = false;
};

removeAllWidgetListeners = () => {
  document.removeEventListener('click', onClickOutside, { capture: true });
  window.removeEventListener('blur', onWindowBlur);
  window.removeEventListener('focus', onWindowFocus);
};

const addClickOutsideListener = () => {
  document.addEventListener('click', onClickOutside, { capture: true });
  window.addEventListener('blur', onWindowBlur);
  window.addEventListener('focus', onWindowFocus);
};

onClickOutside = event => {
  const widgetHolder = document.getElementById('cw-widget-holder');
  if (
    widgetHolder &&
    !widgetHolder.contains(event.target) &&
    window.$chatwoot &&
    window.$chatwoot.isOpen
  ) {
    window.$chatwoot.toggle('close');
    removeAllWidgetListeners();
  }
};

const onWidgetClose = () => {
  removeAllWidgetListeners();
};

const handleSupportClick = async () => {
  if (supportWidgetBaseUrl.value) {
    try {
      await loadRemoteSdk();
      if (window.$chatwoot) {
        if (window.$chatwoot.isOpen) {
          window.$chatwoot.toggle('close');
          removeAllWidgetListeners();
        } else {
          window.$chatwoot.toggle('open');
          window.addEventListener('chatwoot:close', onWidgetClose, {
            once: true,
          });
          setTimeout(addClickOutsideListener, 300);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Billing] Failed to load support SDK:', e);
    }
  } else if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

onMounted(() => {
  fetchBillingData();
  fetchLimits();
});

onBeforeUnmount(() => {
  if (removeAllWidgetListeners) removeAllWidgetListeners();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="!hasPlan && !isLoading"
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <!-- Current Plan Card -->
        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <div
            v-if="hasPlan"
            class="grid lg:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-4 divide-x divide-n-weak px-5 pb-4"
          >
            <div class="flex flex-col pl-3">
              <span
                class="text-xs uppercase tracking-wide text-n-slate-11 mb-1"
              >
                {{ $t('BILLING_SETTINGS.PLAN.LABEL') }}
              </span>
              <span class="text-xl font-bold text-n-slate-12">
                {{ planName }}
              </span>
            </div>
            <div class="flex flex-col pl-5">
              <span
                class="text-xs uppercase tracking-wide text-n-slate-11 mb-1"
              >
                {{ $t('BILLING_SETTINGS.PLAN.CYCLE_LABEL') }}
              </span>
              <span class="text-xl font-bold text-n-slate-12">
                {{ billingCycleLabel }}
              </span>
            </div>
            <div class="flex flex-col pl-5">
              <span
                class="text-xs uppercase tracking-wide text-n-slate-11 mb-1"
              >
                {{ $t('BILLING_SETTINGS.PLAN.PRICE_LABEL') }}
              </span>
              <span class="text-xl font-bold text-n-slate-12">
                {{ planPrice }}
              </span>
            </div>
            <div class="flex flex-col pl-5">
              <span
                class="text-xs uppercase tracking-wide text-n-slate-11 mb-1"
              >
                {{ $t('BILLING_SETTINGS.PLAN.DUE_DATE_LABEL') }}
              </span>
              <span class="text-xl font-bold text-n-slate-12">
                {{ dueDateLabel }}
              </span>
            </div>
          </div>
          <div v-if="showRenewalButton" class="flex justify-end px-5 pb-4">
            <button
              class="inline-flex items-center gap-2 rounded-lg bg-woot-500 hover:bg-woot-600 text-white font-semibold text-sm px-4 py-2 transition-colors"
              @click="handleRenewalClick"
            >
              {{ $t('BILLING_SETTINGS.PLAN.RENEW_BUTTON') }}
            </button>
          </div>
        </BillingCard>

        <!-- Usage Meters -->
        <BillingCard
          v-if="hasPlan"
          :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
        >
          <div class="px-5 pb-2 space-y-3">
            <BillingMeter
              v-if="agentsMeter.total > 0"
              :title="$t('BILLING_SETTINGS.USAGE.AGENTS')"
              :total-count="agentsMeter.total"
              :current-available="agentsMeter.total - agentsMeter.consumed"
              :consumed="agentsMeter.consumed"
            />
            <BillingMeter
              v-if="inboxesMeter.total > 0"
              :title="$t('BILLING_SETTINGS.USAGE.INBOXES')"
              :total-count="inboxesMeter.total"
              :current-available="inboxesMeter.total - inboxesMeter.consumed"
              :consumed="inboxesMeter.consumed"
            />
            <BillingMeter
              v-if="conversationsMeter.total > 0"
              :title="$t('BILLING_SETTINGS.USAGE.CONVERSATIONS')"
              :total-count="conversationsMeter.total"
              :current-available="
                conversationsMeter.total - conversationsMeter.consumed
              "
              :consumed="conversationsMeter.consumed"
            />
            <BillingMeter
              v-if="aiCreditsMeter.total > 0"
              :title="$t('BILLING_SETTINGS.USAGE.AI_CREDITS')"
              :total-count="aiCreditsMeter.total"
              :current-available="
                aiCreditsMeter.total - aiCreditsMeter.consumed
              "
              :consumed="aiCreditsMeter.consumed"
            />
          </div>
        </BillingCard>

        <!-- Captain Credits (existing) -->
        <BillingCard
          v-if="captainEnabled"
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              flushed
              slate
              icon="i-lucide-refresh-cw"
              :is-loading="isFetchingLimits"
              @click="fetchLimits"
            >
              {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
            </ButtonV4>
          </template>
          <div v-if="captainLimits && responseLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.RESPONSES')"
              v-bind="responseLimits"
            />
          </div>
          <div v-if="captainLimits && documentLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS')"
              v-bind="documentLimits"
            />
          </div>
        </BillingCard>

        <!-- Support -->
        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="handleSupportClick"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
    </template>
  </SettingsLayout>
</template>
